import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/misc';
import { ThemeProvider, useTheme } from '@/lib/theme';
import { useIsWide } from '@/lib/use-media-query';
import { ApiError } from '@/lib/api';
import { lockViewport } from '@/lib/lock-viewport';
import { AppRoutes } from './router';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx erneut zu versuchen bringt nichts.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

// Unter der Kopfzeile (4.5rem) plus Luft. Seitlich derselbe Rand wie der
// Seiteninhalt, damit die Meldung nicht schmaler wirkt als alles darunter.
const MOBILE_OFFSET = {
  top: 'calc(4.5rem + env(safe-area-inset-top) + 0.5rem)',
  left: '0.75rem',
  right: '0.75rem',
};

function ThemedToaster() {
  const { resolved } = useTheme();
  const wide = useIsWide();

  // Unten rechts ist am Schreibtisch die ruhigste Ecke – auf dem Telefon ist
  // sie besetzt: dort sitzen die Tab-Leiste und die Werkzeuge, mit denen man
  // gerade arbeitet. Beim Markieren von Gesichtern legt sich die Rückmeldung
  // genau über das Bild. Deshalb kommt sie schmal unter die Kopfzeile, wo
  // nichts liegt, was man im selben Moment bedient.
  return (
    <Toaster
      theme={resolved}
      position={wide ? 'bottom-right' : 'top-center'}
      offset={wide ? undefined : MOBILE_OFFSET}
      mobileOffset={wide ? undefined : MOBILE_OFFSET}
      closeButton
      richColors={false}
      toastOptions={{ className: 'rounded-xl border border-line bg-surface text-ink shadow-float' }}
    />
  );
}

// Vor dem ersten Rendern: die Seite als Ganzes zoomt und dreht sich nicht –
// gezoomt wird nur im Bild und im Stammbaum.
lockViewport();

const root = document.getElementById('root');
if (!root) throw new Error('#root fehlt im HTML');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={350} skipDelayDuration={200}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <ThemedToaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
